import { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { captureException } from '@sentry/react';
import PDFMerger from 'pdf-merger-js';
import { downloadFile, fetchFile } from 'further-ui/utils';
import { TaxReport } from 'further-ui/components';
import api from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';

const addCertificatesToPdf = async (
  pdfMerger: PDFMerger,
  certificateUrls: Array<string>,
) => {
  const certificates: Array<ArrayBuffer> = (
    await Promise.all(
      certificateUrls.map(async (certificateUrl) => {
        let certificateData: ArrayBuffer | null = null;

        if (certificateUrl) {
          try {
            certificateData = await fetchFile(certificateUrl);
          } catch (e) {
            captureException(e);
          }
        }

        return certificateData;
      }),
    )
  ).filter((certificate) => certificate !== null);

  for (const certificate of certificates) {
    try {
      await pdfMerger.add(certificate);
    } catch (e) {
      captureException(e);
    }
  }
};

const useTaxReportDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const notification = useNotification();
  const utils = api.useUtils();

  const fetchReport = async (taxYearStart: number, investorId: string) => {
    const result = await utils.investor.getInvestorTaxReport.fetch({
      investorId,
      taxYearStart,
    });
    return result;
  };

  const downloadReport = async (taxYearStart: number, investorId: string) => {
    setIsDownloading(true);
    try {
      const result = await fetchReport(taxYearStart, investorId);

      if (!result) {
        throw new Error('No data received from the server');
      }

      try {
        const pdfMerger = new PDFMerger();
        const pdfBlob = await pdf(
          <TaxReport
            reportComponents={result.components}
            firmLogoUrl={result.components.firmLogoUrl}
          />,
        ).toBlob();
        const arrayBuffer = await pdfBlob.arrayBuffer();
        await pdfMerger.add(arrayBuffer);

        await addCertificatesToPdf(pdfMerger, result.certificates);

        downloadFile(
          new Blob([
            (await pdfMerger.saveAsBuffer()) as Uint8Array<ArrayBuffer>,
          ]),
          `${
            result.components.investorFullName
          } - Annual tax report ${result.components.taxYear
            .split('/')
            .join('-')}.pdf`,
        );

        notification.success('Tax report downloaded successfully.');
      } catch (pdfError) {
        console.error('PDF Generation Error:', pdfError);
        throw pdfError;
      }
    } catch (error) {
      captureException(error);
      notification.error(
        "Your tax report can't be downloaded at this time. Please contact support@joinfurther.com for assistance.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadReport,
    isDownloading,
  };
};

export default useTaxReportDownload;
